import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Layout from '../components/Layout'
import { listMyOrders } from '../lib/myOrders'
import { CLIENT_STEPS, PAYMENT_LINK, clientStepIndex, clientStepLabel, paymentBadge } from '../lib/tracking'
import { fmtMoney } from '../lib/pricing'
import { shortDate } from '../lib/dates'
import { SERVICE_LABEL } from '../lib/orders'
import InstallPrompt from '../components/InstallPrompt'

const VISITS_KEY = 'my_orders_visits'

// My orders — spec §5. Lists the customer's orders with the visible
// client frieze, plus the payment badge / bar when the order is stuck
// in Processing waiting for the customer to settle.
export default function MyOrders() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // The install banner also lives here, from the SECOND visit on. It used to
  // exist only on /confirmation, so a customer who hadn't ordered yet never
  // saw it at all. Someone opening "My orders" twice is a returning
  // customer — they have a reason to want the icon on their home screen.
  // Still never on /order: we don't interrupt a booking in progress.
  const [showInstall, setShowInstall] = useState(false)
  useEffect(() => {
    try {
      const n = Number(localStorage.getItem(VISITS_KEY) || 0) + 1
      localStorage.setItem(VISITS_KEY, String(n))
      if (n >= 2) setShowInstall(true)
    } catch { /* private mode — just don't offer */ }
  }, [])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const data = await listMyOrders({ limit: 50 })
        if (alive) setRows(data)
      } catch (err) {
        if (alive) setError(err?.message || 'Could not load your orders')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  return (
    <Layout>
      <div className="px-5 pt-5">
        <h1 className="font-bricolage text-[20px] font-extrabold text-navy">My orders</h1>
        <p className="mt-1 text-[13px] text-ink-2">
          Track your pickups from booking to delivery.
        </p>
      </div>

      <div className="mt-4 space-y-3 px-4">
        {loading && <p className="p-4 text-center text-[12px] text-ink-2">Loading…</p>}
        {error && (
          <div className="rounded-xl border border-red bg-red/10 p-3 text-[12px] font-bold text-red">
            {error}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="rounded-xl border border-line bg-white p-6 text-center">
            <p className="text-[13px] text-ink-2">You don&apos;t have any orders yet.</p>
            <Link to="/order" className="mt-3 inline-block text-[13px] font-bold text-navy underline">
              Book your first pickup →
            </Link>
          </div>
        )}
        {groupRows(rows).map((entry) =>
          entry.group
            ? <PickupGroupCard key={entry.key} orders={entry.orders} />
            : <OrderCard key={entry.key} row={entry.orders[0]} />,
        )}
      </div>

      {showInstall && <InstallPrompt />}
    </Layout>
  )
}

// Orders sharing a pickup_group_id were one pickup — show them as one card.
// Rows keep their DB order (newest first), and a group takes the position of
// its most recent order.
function groupRows(rows) {
  const out = []
  const seen = new Map()
  for (const r of rows) {
    if (!r.pickup_group_id) {
      out.push({ key: r.order_number, group: false, orders: [r] })
      continue
    }
    const at = seen.get(r.pickup_group_id)
    if (at == null) {
      seen.set(r.pickup_group_id, out.length)
      out.push({ key: r.pickup_group_id, group: true, orders: [r] })
    } else {
      out[at].orders.push(r)
    }
  }
  // A group that ended up with a single visible order is just an order.
  return out.map((e) => (e.group && e.orders.length < 2 ? { ...e, group: false } : e))
}

// One pickup, several services. The return line is written once, at the
// bottom: it's the same for the whole group.
function PickupGroupCard({ orders }) {
  const head = orders[0]
  const stepIdx = Math.min(...orders.map((o) => clientStepIndex(o.status)))
  const anyDue = orders.some((o) => paymentBadge(o.status, o.is_paid)?.kind === 'due')
  const dueTotal = orders.reduce((s, o) => s + Number(o.final_total_cents || 0), 0)
  const allCancelled = orders.every((o) => o.status === 'cancelled')

  return (
    <div className="rounded-xl border border-line bg-white p-3">
      <div className="flex items-baseline justify-between gap-2">
        <div className="font-bricolage text-[13px] font-extrabold text-navy">
          {head.pickup_date ? `Pickup · ${shortDate(head.pickup_date)}` : 'Pickup'}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-ink-3">
          {orders.length} services
        </div>
      </div>

      <div className="mt-2 space-y-1">
        {orders.map((o) => {
          const badge = paymentBadge(o.status, o.is_paid)
          return (
            <Link
              key={o.order_number}
              to={`/orders/${encodeURIComponent(o.order_number)}`}
              className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-[#F9FAFB]"
            >
              <span className="flex min-w-0 items-center gap-2">
                <span className="font-bricolage text-[12px] font-extrabold text-navy">
                  #{o.order_number}
                </span>
                <span className="truncate text-[12px] text-ink-2">
                  {SERVICE_LABEL[o.service] || o.service}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {badge && <PaymentBadge badge={badge} />}
                <span aria-hidden className="text-[11px] text-ink-3">›</span>
              </span>
            </Link>
          )
        })}
      </div>

      {!allCancelled && (
        <>
          <StatusFrieze current={stepIdx} />
          <div className="mt-2 text-[11px] text-ink-2">
            Return confirmed when everything is ready
          </div>
        </>
      )}

      {anyDue && <PaymentDueBar amountCents={dueTotal} />}
    </div>
  )
}

function OrderCard({ row }) {
  const stepIdx = clientStepIndex(row.status)
  const badge = paymentBadge(row.status, row.is_paid)
  const isCancelled = row.status === 'cancelled'

  return (
    <Link
      to={`/orders/${encodeURIComponent(row.order_number)}`}
      className="block rounded-xl border border-line bg-white p-3 hover:bg-[#F9FAFB]"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="font-bricolage text-[13px] font-extrabold text-navy">
            #{row.order_number}
          </div>
          {badge && <PaymentBadge badge={badge} />}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-ink-3">
          {SERVICE_LABEL[row.service] || row.service}
        </div>
      </div>

      <div className="mt-1 text-[13px] font-bold text-navy">
        {isCancelled ? 'Cancelled' : clientStepLabel(row.status)}
      </div>
      <div className="mt-0.5 text-[11px] text-ink-2">
        {row.pickup_date ? `Pickup ${shortDate(row.pickup_date)}` : ''}
      </div>

      {!isCancelled && <StatusFrieze current={stepIdx} />}

      {badge?.kind === 'due' && (
        <PaymentDueBar amountCents={row.final_total_cents} />
      )}
    </Link>
  )
}

function PaymentBadge({ badge }) {
  const style = badge.kind === 'paid'
    ? { color: '#15803D', background: '#DCFCE7', border: '1px solid #86EFAC' }
    : { color: '#D4A017', background: '#FEF6DF', border: '1px solid #F0DCA0' }
  return (
    <span
      style={{ ...style, padding: '2px 8px', borderRadius: 999, fontSize: '9px', fontWeight: 800, letterSpacing: '0.02em', textTransform: 'uppercase' }}
    >
      {badge.text}
    </span>
  )
}

// The 5-step frieze. Steps up to `current` are filled navy; the rest
// stay pale. A cancelled order is rendered by the caller as "Cancelled"
// instead.
function StatusFrieze({ current }) {
  return (
    <ol className="mt-3 flex items-center gap-1" aria-label="Order progress">
      {CLIENT_STEPS.map((s, i) => {
        const done = i <= current
        return (
          <li key={s.key} className="flex flex-1 items-center gap-1">
            <span
              aria-hidden
              className="h-1.5 flex-1 rounded-full"
              style={{ background: done ? '#0B2038' : '#E5E7EB' }}
            />
            {i === CLIENT_STEPS.length - 1 && (
              <span
                aria-hidden
                className="h-2 w-2 rounded-full"
                style={{ background: done ? '#0B2038' : '#E5E7EB' }}
              />
            )}
          </li>
        )
      })}
    </ol>
  )
}

function PaymentDueBar({ amountCents }) {
  return (
    <div
      className="mt-3 flex items-center justify-between rounded-lg px-3 py-2"
      style={{ background: '#FEF6DF', border: '1px solid #F0DCA0' }}
      onClick={(e) => e.preventDefault()}
    >
      <div className="min-w-0">
        <div className="text-[12px] font-extrabold text-navy">💳 Waiting for your payment</div>
        <div className="mt-0.5 text-[11px] leading-relaxed text-navy/80">
          Weighed and priced. We&apos;ll reach out to settle it, then it goes back out.
        </div>
      </div>
      <div className="ml-3 flex flex-col items-end gap-1">
        <span className="font-bricolage text-[14px] font-extrabold text-navy">
          {fmtMoney(amountCents)}
        </span>
        {PAYMENT_LINK && (
          // Reserved slot — flipped on when Stripe is wired up.
          <button
            type="button"
            className="rounded-md bg-navy px-2.5 py-1 text-[10px] font-extrabold text-white"
            onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
          >
            Pay now
          </button>
        )}
      </div>
    </div>
  )
}
